import { useContract, useShareMetrics } from '../../api/hooks';

export interface InferredWizardStep {
  isLoading: boolean;
  /** The step to land on, or null while loading. */
  step: 1 | 2 | 3 | 4 | null;
  /** Set when the contract is no longer a draft — the caller should redirect instead of rendering a step. */
  redirectTo: string | null;
}

/**
 * Infers which wizard step to resume a contract on, from server state alone —
 * no separate "job progress" is stored. Step 1 is never inferred (a contract
 * only exists once Step 1 has already run); Step 4 is only ever reached by
 * the user clicking Continue from Step 3, never auto-inferred.
 */
export function useInferWizardStep(contractId: string | undefined): InferredWizardStep {
  const contract = useContract(contractId);
  const shareMetrics = useShareMetrics(contractId);

  if (contract.isLoading || shareMetrics.isLoading) {
    return { isLoading: true, step: null, redirectTo: null };
  }

  if (contract.data?.status !== undefined && contract.data.status !== 'draft') {
    return { isLoading: false, step: null, redirectTo: '/authorised-contracts' };
  }

  if (contract.data?.showId == null) {
    return { isLoading: false, step: 2, redirectTo: null };
  }

  const totalPercentAllocated = shareMetrics.data?.totalPercentAllocated ?? 0;
  if (totalPercentAllocated === 0) {
    return { isLoading: false, step: 2, redirectTo: null };
  }

  return { isLoading: false, step: 3, redirectTo: null };
}
